import type {
  AgeCategory,
  AgeCategoryTable,
  DocumentCategory,
  DocumentDiscipline,
  FormationSize,
  PerformanceClass,
  Recording,
  RuleSetDocument,
} from "./queries/rules";
import { ageCategoryAdmits } from "./entryChecks";
import type { ID } from "./id";

/**
 * Lookup structures over the published rule-set document, shared by
 * every surface that renders a combination ("MD Dance · Sólo · Junior
 * · A") or lets the user pick one.
 */

export interface ComboRef {
  discipline: DocumentDiscipline;
  category: DocumentCategory;
  ageCategory?: AgeCategory;
  formationSize?: FormationSize;
  performanceClass?: PerformanceClass;
  /** The official ČNSMS track, already resolved
   * combination-override-else-discipline-default — one discipline can
   * need more than one (TD Twirl duo/trio vs tým), so this must never be
   * looked up per discipline. Only meaningful when the discipline's
   * `music_source` is `federation`; undefined there means the rule set
   * is unfinished, which is a rule-set problem rather than a
   * competition one. */
  recording?: Recording;
}

export interface DocumentIndex {
  ageCategories: Map<ID, AgeCategory>;
  ageTables: Map<ID, AgeCategoryTable>;
  formationSizes: Map<ID, FormationSize>;
  performanceClasses: Map<ID, PerformanceClass>;
  recordings: Map<ID, Recording>;
  /** DisciplineCategory id → the resolved combination. */
  combos: Map<ID, ComboRef>;
}

export function indexDocument(doc: RuleSetDocument): DocumentIndex {
  const ageCategories = new Map<ID, AgeCategory>();
  const ageTables = new Map<ID, AgeCategoryTable>();
  for (const t of doc.age_tables) {
    ageTables.set(t.id, t);
    for (const c of t.categories ?? []) ageCategories.set(c.id, c);
  }
  const formationSizes = new Map<ID, FormationSize>();
  for (const t of doc.formation_tables) {
    for (const s of t.sizes ?? []) formationSizes.set(s.id, s);
  }
  const performanceClasses = new Map<ID, PerformanceClass>();
  for (const t of doc.performance_class_tables) {
    for (const c of t.classes ?? []) performanceClasses.set(c.id, c);
  }
  const recordings = new Map<ID, Recording>();
  for (const r of doc.recordings ?? []) recordings.set(r.id, r);
  const combos = new Map<ID, ComboRef>();
  for (const d of doc.disciplines) {
    for (const c of d.categories) {
      combos.set(c.id, {
        discipline: d,
        category: c,
        ageCategory: ageCategories.get(c.age_category_id),
        formationSize: formationSizes.get(c.formation_size_id),
        performanceClass: performanceClasses.get(c.performance_class_id),
        recording:
          c.recording_id === undefined ? undefined : recordings.get(c.recording_id),
      });
    }
  }
  return {
    ageCategories,
    ageTables,
    formationSizes,
    performanceClasses,
    recordings,
    combos,
  };
}

/**
 * Picks the combination a group of the given aggregated age enters,
 * among the candidates of one discipline × formation × class: the
 * youngest category that admits the age (upper bound only — younger
 * groups start up, older ones are never admitted down). Returns
 * undefined when the age exceeds every offered band.
 */
export function pickComboForAge(candidates: ComboRef[], age: number): ComboRef | undefined {
  return candidates
    .filter((r) => r.ageCategory && ageCategoryAdmits(r.ageCategory, age))
    .sort(
      (a, b) =>
        (a.ageCategory!.min_age ?? Number.NEGATIVE_INFINITY) -
          (b.ageCategory!.min_age ?? Number.NEGATIVE_INFINITY) ||
        a.ageCategory!.sort_order - b.ageCategory!.sort_order,
    )[0];
}

/** The federation's result-list key: "MD Dance · Sólo · Junior mladší · A". */
export function comboLabel(ref: ComboRef): string {
  return [
    ref.discipline.name,
    ref.formationSize?.name,
    ref.ageCategory?.name,
    ref.performanceClass?.code,
  ]
    .filter(Boolean)
    .join(" · ");
}
